// The calendar, on the client.
//
// The server computes every date (models/schedule.go); this decides how a list
// of dated sessions reads as "today", "tomorrow" and "this week".
//
// # AN EMPTY CALENDAR IS A STATE
//
// An athlete whose assignment has no start date, or who trains a programme
// nobody assigned them, has no schedule at all. That is ordinary — not a
// failure, not an empty-list bug — and the least-recently-trained ordering
// remains the honest answer for them. Anything rendering this has to say so.

export const WEEKDAYS = [
  { value: 0, name: 'Sunday', short: 'Sun' },
  { value: 1, name: 'Monday', short: 'Mon' },
  { value: 2, name: 'Tuesday', short: 'Tue' },
  { value: 3, name: 'Wednesday', short: 'Wed' },
  { value: 4, name: 'Thursday', short: 'Thu' },
  { value: 5, name: 'Friday', short: 'Fri' },
  { value: 6, name: 'Saturday', short: 'Sat' },
];

/** Today as YYYY-MM-DD in the reader's own timezone. */
export function todayISO(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * What is scheduled for today.
 *
 * More than one is normal — two blocks, or a squad calendar beside a personal
 * copy — so this returns a list rather than the first match.
 */
export function dueToday(entries, today = todayISO()) {
  return (entries ?? []).filter((e) => e.date === today);
}

/**
 * The next thing scheduled after today, or null.
 *
 * Entries arrive in date order from the server, so the first future one wins.
 */
export function nextUp(entries, today = todayISO()) {
  return (entries ?? []).find((e) => e.date > today) ?? null;
}

/**
 * Anything scheduled before today that was never trained.
 *
 * # WHY "MISSED" IS NOT SIMPLY "IN THE PAST"
 *
 * A session scheduled on Monday and trained on Monday is done. One scheduled on
 * Monday and trained on Tuesday is also done — the athlete moved it, which is
 * normal and not a failure. So a date is missed only when nothing was logged
 * against that session on OR AFTER the day it was due.
 *
 * Getting this wrong in the lenient direction (any training counts) hides real
 * gaps; getting it wrong in the strict direction (trained on the exact day)
 * scolds an athlete for training Tuesday instead of Monday.
 */
export function missed(entries, today = todayISO()) {
  return (entries ?? []).filter((e) => {
    if (e.date >= today) return false;
    if (!e.lastTrainedOn) return true;
    return e.lastTrainedOn < e.date;
  });
}

/** Group dated entries by their date, in order. */
export function byDate(entries) {
  const groups = new Map();
  for (const entry of entries ?? []) {
    if (!groups.has(entry.date)) groups.set(entry.date, []);
    groups.get(entry.date).push(entry);
  }
  return [...groups.entries()].map(([date, items]) => ({ date, entries: items }));
}

/**
 * How a scheduled date reads relative to today.
 *
 * "Today" and "Tomorrow" are what an athlete is actually looking for; anything
 * further out is a weekday name, which is how people talk about the week ahead.
 */
export function dateLabel(iso, today = todayISO()) {
  if (!iso) return '';
  // An unparseable value renders as nothing rather than as a plausible-looking
  // date built from its fragments.
  if (!parseISO(iso)) return '';
  if (iso === today) return 'Today';

  const days = daysBetween(today, iso);
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days < 7) return weekdayOf(iso);
  if (days < -1 && days > -7) return `Last ${weekdayOf(iso)}`;

  const [, month, day] = iso.split('-');
  return `${Number(day)} ${monthName(Number(month))}`;
}

function daysBetween(fromISO, toISO) {
  const a = parseISO(fromISO);
  const b = parseISO(toISO);
  if (!a || !b) return 0;
  return Math.round((b - a) / 86400000);
}

/** Parsed as a LOCAL date — `new Date('2026-08-31')` is UTC midnight and shifts. */
function parseISO(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function weekdayOf(iso) {
  const d = parseISO(iso);
  return d ? WEEKDAYS[d.getDay()].name : '';
}

function monthName(n) {
  return ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'][n - 1] ?? '';
}

/** The short name for a weekday value, for a picker. */
export function weekdayShort(value) {
  const found = WEEKDAYS.find((d) => d.value === value);
  return found ? found.short : '';
}
